import { Badge, Card, CardContent, CardHeader, CardTitle, Tooltip } from '@chain-zen/ui'
import { format, parse } from 'date-fns'

import type { ICompany } from '../../types/company'

/**
 * CompanyDetails component is used to display the chat details of the shipment and user
 * @returns ChatDetails component
 */
export const CompanyDetails = ({ company }: { company: ICompany }) => {
  const formatDate = (dateString: string) => {
    try {
      const parsedDate = parse(dateString, 'dd-MMM-yy', new Date())
      return format(parsedDate, 'MMM dd, yyyy')
    } catch (error) {
      console.error('Error parsing date:', error)
      return dateString
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>FMCSA Info</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex flex-col gap-4">
          <div className="flex flex-col items-start w-full gap-2">
            {!!company?.companyName && (
              <div className="flex w-full items-center justify-between text-sm">
                <span className="text-muted-foreground">Name: </span>
                <span className="font-semibold">{company?.companyName}</span>
              </div>
            )}
            {!!company?.mcsData?.mcisAddDate && (
              <div className="flex w-full items-center justify-between text-sm">
                <span className="text-muted-foreground">Registered: </span>
                <span className="font-semibold">{formatDate(company?.mcsData?.mcisAddDate)}</span>
              </div>
            )}
            {!!company?.phoneNumber && (
              <div className="flex w-full items-center justify-between text-sm">
                <span className="text-muted-foreground">Phone: </span>
                <span className="font-semibold">{company?.phoneNumber}</span>
              </div>
            )}
            {!!company?.emailAddress && (
              <div className="flex w-full items-center justify-between text-sm">
                <span className="text-muted-foreground">Email: </span>
                <span className="font-semibold truncate">{company?.emailAddress}</span>
              </div>
            )}
            {!!company?.faxNumber && (
              <div className="flex w-full items-center justify-between text-sm">
                <span className="text-muted-foreground">Fax: </span>
                <span className="font-semibold">{company?.faxNumber}</span>
              </div>
            )}
            {!!company?.id && (
              <div className="flex w-full items-center justify-between text-sm">
                <span className="text-muted-foreground">DOT: </span>
                <span className="font-semibold">{company?.id}</span>
              </div>
            )}
            {!!company?.mcNumber && company?.mcNumber !== 'undefined' && (
              <div className="flex w-full items-center justify-between text-sm">
                <span className="text-muted-foreground">MC: </span>
                <span className="font-semibold">{company?.mcNumber}</span>
              </div>
            )}
          </div>

          {/* Operation Section */}
          <div className="flex flex-col space-y-2 items-start w-full">
            <span className="font-semibold">Operation</span>
            <div className="flex flex-col items-start w-full gap-2">
              <Tooltip content="This means the DOT is active">
                <div className="flex w-full items-center justify-between text-sm">
                  <span className="text-muted-foreground">Allowed to operate: </span>
                  <Badge
                    variant={company?.allowedToOperate ? 'default' : 'destructive'}
                    aria-label="allowed-to-operate"
                  >
                    {company?.allowedToOperate ? 'Yes' : 'No'}
                  </Badge>
                </div>
              </Tooltip>

              <Tooltip content="Brokerage operation">
                <div className="flex w-full items-center justify-between text-sm">
                  <span className="text-muted-foreground" aria-label="operation-broker">
                    Broker:{' '}
                  </span>
                  <Badge
                    variant={company?.authorizedForBroker ? 'default' : 'destructive'}
                    aria-label="authorized-broker"
                  >
                    {company?.authorizedForBroker ? 'Yes' : 'No'}
                  </Badge>
                </div>
              </Tooltip>

              <Tooltip content="An authorized for-hire Motor Carrier that transports regulated commodities">
                <div className="flex w-full items-center justify-between text-sm">
                  <span className="text-muted-foreground" aria-label="operation-carrier">
                    Carrier:{' '}
                  </span>
                  <Badge
                    variant={company?.authorizedForProperty ? 'default' : 'destructive'}
                    aria-label="authorized-for-property"
                  >
                    {company?.authorizedForProperty ? 'Yes' : 'No'}
                  </Badge>
                </div>
              </Tooltip>

              {!!company?.numberOfPowerUnits && (
                <div className="flex w-full items-center justify-between text-sm">
                  <span className="text-muted-foreground" aria-label="operation-power">
                    Power units:{' '}
                  </span>
                  <span className="text-sm">{company?.numberOfPowerUnits}</span>
                </div>
              )}
              {!!company?.numberOfDrivers && (
                <div className="flex w-full items-center justify-between text-sm">
                  <span className="text-muted-foreground" aria-label="operation-driver">
                    Drivers:{' '}
                  </span>
                  <span className="text-sm">{company?.numberOfDrivers}</span>
                </div>
              )}
            </div>
          </div>

          {/* Authority Section */}
          <div className="flex flex-col space-y-2 items-start w-full">
            <span className="font-semibold">Authority</span>
            <div className="flex flex-col items-start w-full gap-2">
              {!!company?.brokerAuthorityStatus && (
                <div className="flex w-full items-center justify-between text-sm">
                  <span className="text-muted-foreground" aria-label="authority-broker">
                    Broker:{' '}
                  </span>
                  <Badge variant="secondary" aria-label="broker-authority-status">
                    {company?.brokerAuthorityStatus}
                  </Badge>
                </div>
              )}
              {!!company?.commonAuthorityStatus && (
                <div className="flex w-full items-center justify-between text-sm">
                  <span className="text-muted-foreground" aria-label="authority-carrier">
                    Carrier:{' '}
                  </span>
                  <Badge variant="secondary" aria-label="common-authority-status">
                    {company?.commonAuthorityStatus}
                  </Badge>
                </div>
              )}
              {!!company?.contractAuthorityStatus && (
                <div className="flex w-full items-center justify-between text-sm">
                  <span className="text-muted-foreground" aria-label="authority-contract">
                    Contract:{' '}
                  </span>
                  <Badge variant="secondary" aria-label="contract-authority-status">
                    {company?.contractAuthorityStatus}
                  </Badge>
                </div>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
